import { useMemo } from "react";
import type { Indicator } from "../types";
import { buildSparklinePath, extractSparklineValues, trendTone } from "../utils";

const KPI_CODE_PREFIXES: Record<string, string> = {
  market_base: "MB",
  household_consumption: "HC",
  consumer_mind: "CM",
  retail_sales: "RS",
  price_environment: "PE",
};

function kpiCode(indicator: Indicator, index: number): string {
  const prefix = KPI_CODE_PREFIXES[indicator.id] ?? indicator.id.slice(0, 2).toUpperCase();
  return `${prefix}·${String(index + 1).padStart(2, "0")}`;
}

function stripUnitSuffix(value: string, unit: string): string {
  return unit && value.endsWith(unit) ? value.slice(0, value.length - unit.length) : value;
}

function MetricCard({ indicator, index }: { indicator: Indicator; index: number }) {
  // KPIの推移スパークラインは「時系列(target)」チャートの既定系列(実績値)から
  // 取る。共時的分析(source: 地図/構成バー/横棒/レーダー/バブル)は都道府県別・
  // 費目別・業種別などの断面スナップショットであり、時系列トレンドではない上、
  // レーダー/バブルは値が配列型のため抽出できず空欄になる。target側は全指標で
  // 実際の月次/年次推移(直近24件、既に期間統一済み)を持つため、5指標すべてで
  // 一貫した本物のトレンドを表示できる。
  const trendChart = indicator.charts.find((chart) => chart.role === "target") ?? indicator.charts[0];
  const sparklineValues = useMemo(() => extractSparklineValues(trendChart), [trendChart]);
  const sparklinePath = useMemo(() => buildSparklinePath(sparklineValues), [sparklineValues]);
  const tone = trendTone(indicator.latest.deltaDirection);

  return (
    <a className="kpi" href={`#${indicator.id}`} aria-label={`${indicator.shortTitle}の詳細へ移動`}>
      <span className="kpi__tick" aria-hidden="true" />
      <div className="kpi__topline">
        <span>{kpiCode(indicator, index)}</span>
        <span>{indicator.latest.period}</span>
      </div>
      <div className="kpi__category">{indicator.category}</div>
      <h2 className="kpi__title">{indicator.shortTitle}</h2>
      <div className="kpi__value-row">
        <span className="kpi__value">{stripUnitSuffix(indicator.latest.value, indicator.latest.unit)}</span>
        <span className="kpi__unit">{indicator.latest.unit}</span>
      </div>
      <p className={`kpi__delta ${tone}`}>{indicator.latest.deltaLabel}</p>
      {sparklinePath ? (
        <svg className={`kpi__spark ${tone}`} viewBox="0 0 100 30" preserveAspectRatio="none" aria-hidden="true">
          <path
            d={sparklinePath}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.4}
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      ) : null}
    </a>
  );
}

export { MetricCard, KPI_CODE_PREFIXES, kpiCode, stripUnitSuffix };
